import { ExSideDrawer } from '@boomi/exosphere';
import { type ComponentRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDrawerHeaderOffset } from '../app-shell/headerHeight';
import ConnectorDetail from '../connectors/ConnectorDetail';
import ConnectorList from '../connectors/ConnectorList';
import ConnectionCreate from './ConnectionCreate';
import styles from './ConnectionDrawer.module.css';
import ConnectionView from './ConnectionView';

export type DrawerMode =
    | { type: 'create-connection'; connectorId?: string; fromList?: boolean }
    | { type: 'view-connection'; connectorId: string; connectionId: string }
    | { type: 'view-connector'; connectorId: string };

interface ConnectionDrawerProps {
    mode: DrawerMode | null;
    onClose: () => void;
    onConnectorView: (connectorId: string) => void;
    onConnectorSelect: (connectorId: string, fromList?: boolean) => void;
    onConnectionSelect: (appId: string) => void;
    onBack?: () => void;
}

const useClientSecret = ({ clearWhen }: { clearWhen: boolean[] }) => {
    const [clientSecret, setClientSecret] = useState('');

    useEffect(() => {
        const clearSecret = clearWhen.some((condition) => condition);

        if (clearSecret) {
            setClientSecret('');
        }
    }, [clearWhen]);

    useEffect(() => {
        return () => setClientSecret('');
    }, []);

    return { clientSecret, setClientSecret };
};

const ConnectionDrawer = ({
    mode,
    onClose,
    onConnectorView,
    onConnectorSelect,
    onConnectionSelect,
    onBack,
}: ConnectionDrawerProps) => {
    const [isOpen, setIsOpen] = useState(mode !== null);
    const drawerRef = useRef<ComponentRef<typeof ExSideDrawer>>(null);
    const headerHeight = useDrawerHeaderOffset(drawerRef);

    const { clientSecret, setClientSecret } = useClientSecret({
        clearWhen: [
            !isOpen,
            mode?.type === 'view-connector',
            mode?.type === 'create-connection' && !mode.connectorId,
        ],
    });

    useEffect(() => {
        setIsOpen(mode !== null);
    }, [mode]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (mode !== null) {
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.scrollTop = 0;
        }

        return () => {
            document.documentElement.style.overflow = 'auto';
        };
    }, [mode]);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = (e: MouseEvent) => {
            const drawer = drawerRef.current;
            if (drawer && !e.composedPath().includes(drawer)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, handleClose]);

    return (
        <ExSideDrawer
            ref={drawerRef}
            open={isOpen}
            top={headerHeight}
            width="75"
            noMarginSlot
            panelTitle={
                mode?.type === 'create-connection' && !mode.connectorId ? 'Create Config' : ''
            }
            onClose={handleClose}
            className={styles.connectionDrawer}
        >
            {mode?.type === 'create-connection' && !mode.connectorId && (
                <ConnectorList onSelect={(connectorId) => onConnectorSelect(connectorId, true)} />
            )}
            {mode?.type === 'create-connection' && mode.connectorId && (
                <ConnectionCreate
                    onCreate={({ appId, clientSecret }) => {
                        setClientSecret(clientSecret);
                        onConnectionSelect?.(appId);
                    }}
                    connectorId={mode.connectorId}
                    onClose={handleClose}
                    onCancel={() => onConnectorView(mode.connectorId!)}
                    onBack={mode.fromList ? () => onBack?.() : undefined}
                />
            )}
            {mode?.type === 'view-connection' && (
                <ConnectionView
                    connectorId={mode.connectorId}
                    connectionId={mode.connectionId}
                    clientSecret={clientSecret}
                    onClose={() => onConnectorView(mode.connectorId)}
                />
            )}
            {mode?.type === 'view-connector' && (
                <ConnectorDetail
                    connectorId={mode.connectorId}
                    onCreateConfig={() => onConnectorSelect(mode.connectorId)}
                    onBack={() => onBack?.()}
                    onEditConfig={onConnectionSelect}
                />
            )}
        </ExSideDrawer>
    );
};

export default ConnectionDrawer;
